/*LIBRARY*/
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Title,
  SubTitle,
} from "chart.js";
import { Chart } from "react-chartjs-2";

/*COMPONENT*/
import GET_CAPEX from "../trivial_components/GET_CAPEX";
import Modal from "../common_modal/Modal";

/*REDUX*/
import { replace_features } from "../../App/actions/siniActions";
import { filter_isi_layer } from "../../App/actions/dataActions";

/*PICTURE*/

/*FUNCTION*/
import { calculateWithoutConvert } from "../../App/validation/convert_data";
import generate_array_color from "../../App/validation/generate_array_color";

/*DATA*/

/*CONST*/
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  BarElement,
  PointElement,
  BarController,
  BubbleController,
  DoughnutController,
  LineController,
  PieController,
  PolarAreaController,
  RadarController,
  ScatterController,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Decimation,
  Filler,
  Title,
  SubTitle,
);

const CUSTOM_CHART = ({
  layer,
  properties,
  replace_features,
  filter_isi_layer,
  auth,
}) => {
  const [modal_npv, set_modal_npv] = useState(false);
  const [modal_irr, set_modal_irr] = useState(false);
  const [collapsed_folders, set_collapsed_folders] = useState({});

  const toggle_npv = () => {
    set_modal_npv(!modal_npv);
  };

  const toggle_irr = () => {
    set_modal_irr(!modal_irr);
  };

  const toggle_folder = (folder_key) => {
    set_collapsed_folders((prev) => ({
      ...prev,
      [folder_key]: !prev[folder_key],
    }));
  };

  const toggle_all_folders = () => {
    const { layer_id, geo_layer_list } = layer;
    const layer_object = geo_layer_list.find(
      (item) => item?.geo_layer?._id === layer_id,
    )?.geo_layer;

    const f_list = layer_object?.folder_chart_list || [];
    const all_keys = [...f_list.map((f) => f.key), "uncategorized"];

    // If any folder is currently expanded (false/undefined), we collapse all. Otherwise, expand all.
    const is_any_expanded = all_keys.some((key) => !collapsed_folders[key]);

    const new_collapsed_state = {};
    all_keys.forEach((key) => {
      new_collapsed_state[key] = is_any_expanded;
    });

    set_collapsed_folders(new_collapsed_state);
  };

  const calculate_local = (source) => {
    const { layer_id, geo_layer_list } = layer;
    const { capex_value } = properties;
    const layer_object = geo_layer_list.find(
      (item) => item?.geo_layer?._id === layer_id,
    )?.geo_layer;
    const features = layer_object?.geojson?.features;
    const fields = layer_object?.fields;
    if (features && fields && capex_value && layer_id) {
      const features_new = calculateWithoutConvert({
        features,
        fields,
        source,
        capex_value,
      });
      replace_features({
        geo_layer_id: layer_id,
        features: features_new,
      });
    }
  };

  const on_filter_isi_layer = (value, field_key) => {
    const { layer_id } = layer;
    if (layer_id && field_key) {
      const body = {
        geo_layer_id: layer_id,
        field_key,
        value,
      };
      filter_isi_layer(body);
    }
  };

  useEffect(() => {
    calculate_local("componentDidMount");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculate_local("componentDidUpdate");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.capex_value, layer.layer_id]);

  const { layer_id, geo_layer_list } = layer;
  const { capex_value } = properties;

  const layer_object = geo_layer_list.find(
    (item) => item?.geo_layer?._id === layer_id,
  )?.geo_layer;
  let chart_list = layer_object?.chart_list || [];
  const folder_chart_list = layer_object?.folder_chart_list || [];
  const features_filtered = layer_object?.geojson_filtered?.features || [];

  let npv_info;
  let irr_info;

  chart_list = chart_list.map((item) => {
    const {
      ina,
      x_axis_field_key,
      y_axis_field_key,
      x_element_custom_order,
      color_default,
      is_npv_kai,
      wacc_field_key,

      is_use_y_axis,
      is_use_custom_order,
    } = item;

    let labels = [];

    // 1. GENERATE LABELS (Handling \n splits if no custom order is used)
    if (is_use_custom_order) {
      labels = x_element_custom_order;
    } else {
      const temp_labels = new Set(); // Using Set to automatically prevent duplicates
      features_filtered.forEach((feature) => {
        const raw_val = feature?.properties?.[x_axis_field_key];
        if (raw_val) {
          String(raw_val)
            .split("\n")
            .forEach((val) => {
              const clean_val = val.trim();
              if (clean_val) temp_labels.add(clean_val);
            });
        }
      });
      labels = Array.from(temp_labels).sort();
    }

    // Initialize data array with 0s to match the length of our labels
    let data = new Array(labels.length).fill(0);

    // 2. POPULATE DATA (Handling \n splits for accurate tallying)
    features_filtered.forEach((feature) => {
      const raw_x_val = feature?.properties?.[x_axis_field_key];
      if (!raw_x_val) return;

      const split_x_values = String(raw_x_val).split("\n");

      // Loop through each checklist item in the current feature
      split_x_values.forEach((x_val) => {
        const clean_val = x_val.trim();
        const data_index = labels.findIndex(
          (label) => String(label) === clean_val,
        );

        // If the label exists in our chart's X-axis, update its corresponding data point
        if (data_index !== -1) {
          if (is_use_y_axis) {
            const raw_y_val = feature.properties[y_axis_field_key];
            const final_value = isNaN(raw_y_val) ? 0 : parseInt(raw_y_val);
            data[data_index] += final_value;
          } else {
            data[data_index] += 1;
          }
        }
      });
    });

    let npv_value = 0; // Initialize variables to prevent scope errors
    let npv_info = null;
    let irr_value = 0;
    let irr_info = null;

    if (is_npv_kai) {
      function create_npv_function_from_array(array_income, array_wacc) {
        return function y() {
          let result = 0;
          for (let i = 0; i < array_income.length; i++) {
            result += array_income[i] / Math.pow(1 + array_wacc[i], i + 1);
          }
          return result;
        };
      }

      const array_income = data;
      // Note: Ensure array_wacc length matches array_income length to prevent NaN in NPV
      const array_wacc = features_filtered.map(
        (feature_item) =>
          parseFloat(feature_item?.properties?.[wacc_field_key]) / 100,
      );

      const y_function = create_npv_function_from_array(
        array_income,
        array_wacc,
      );
      npv_value = parseInt(y_function());

      const wacc = (
        parseFloat(features_filtered?.[0]?.properties?.[wacc_field_key]) / 100
      ).toFixed(4);

      npv_info = (
        <main>
          <h1 className="text_header margin_bottom">Rumus perhitungan NPV</h1>
          <table
            style={{ backgroundColor: "#ffffff", borderRadius: "7px" }}
            className="table_padding"
          >
            <tbody>
              <tr>
                <td>NPV</td>
                <td style={{ paddingBottom: "7px" }}>=</td>
                <td>
                  {labels.map((year, idx) => {
                    return (
                      <table key={idx} className="inline">
                        <tbody className="text_center">
                          <tr>
                            <td style={{ borderBottom: "1px solid black" }}>
                              Net cash flow {year}
                            </td>
                            <td
                              rowSpan={2}
                              style={{
                                verticalAlign: "center",
                                paddingBottom: "7px",
                              }}
                            >
                              {idx !== labels.length - 1 && " + "}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              ( 1 + WACC) <sup>{idx + 1}</sup>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })}
                </td>
              </tr>
              <tr>
                <td></td>
                <td style={{ paddingBottom: "7px" }}>=</td>
                <td>
                  {data.map((value, idx) => {
                    return (
                      <table key={idx} className="inline">
                        <tbody>
                          <tr>
                            <td style={{ borderBottom: "1px solid black" }}>
                              {new Intl.NumberFormat("id-ID", {
                                style: "decimal",
                              }).format(value)}
                            </td>
                            <td
                              rowSpan={2}
                              style={{
                                verticalAlign: "center",
                                paddingBottom: "7px",
                              }}
                            >
                              {idx !== data.length - 1 && " + "}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              ( 1 + {wacc}) <sup>{idx + 1}</sup>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })}
                </td>
              </tr>
              <tr>
                <td></td>
                <td>=</td>
                <td>
                  {new Intl.NumberFormat("id-ID", {
                    style: "decimal",
                  }).format(npv_value)}
                </td>
              </tr>
            </tbody>
          </table>
          <section className="container_light">
            <p className="text_small">
              Catatan: Net cash flow pada tahun pertama menggunakan nilai
              negatif dari CAPEX penataan, jika nilai CAPEX belum dihitung maka
              perhitungan NPV belum bisa dilakukan dan nilainya menjadi tidak
              sesuai.
            </p>
          </section>
        </main>
      );

      function create_y_function(array_income) {
        return function y(x) {
          let result = 0;
          for (let i = 0; i < array_income.length; i++) {
            result += array_income[i] / Math.pow(1 + x, i + 1);
          }
          return result;
        };
      }

      const y = create_y_function(array_income);

      function find_root_recursive(a, b, tolerance) {
        if (b - a < tolerance) {
          return (a + b) / 2;
        }

        let mid = (a + b) / 2;
        let f_mid = y(mid);

        if (f_mid === 0) {
          return mid;
        } else if (f_mid * y(a) < 0) {
          return find_root_recursive(a, mid, tolerance);
        } else {
          return find_root_recursive(mid, b, tolerance);
        }
      }

      irr_value = find_root_recursive(-1000, 1000, 1e-10);

      irr_info = (
        <main>
          <h1 className="text_header margin_bottom">Rumus perhitungan IRR</h1>
          <p className="text_small">
            Rumus IRR diambil menggunakan rumus NPV dengan menghitung WACC
            ketika NPV sama dengan nol.
          </p>

          <table
            style={{ backgroundColor: "#ffffff", borderRadius: "7px" }}
            className="table_padding"
          >
            <tbody>
              <tr>
                <td>NPV</td>
                <td style={{ paddingBottom: "7px" }}>=</td>
                <td>
                  {labels.map((year, idx) => {
                    return (
                      <table key={idx} className="inline">
                        <tbody className="text_center">
                          <tr>
                            <td style={{ borderBottom: "1px solid black" }}>
                              Net cash flow {year}
                            </td>
                            <td
                              rowSpan={2}
                              style={{
                                verticalAlign: "center",
                                paddingBottom: "7px",
                              }}
                            >
                              {idx !== labels.length - 1 && " + "}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              ( 1 + WACC) <sup>{idx + 1}</sup>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })}
                </td>
              </tr>
              <tr>
                <td>0</td>
                <td style={{ paddingBottom: "7px" }}>=</td>
                <td>
                  {data.map((value, idx) => {
                    return (
                      <table key={idx} className="inline">
                        <tbody>
                          <tr>
                            <td style={{ borderBottom: "1px solid black" }}>
                              {new Intl.NumberFormat("id-ID", {
                                style: "decimal",
                              }).format(value)}
                            </td>
                            <td
                              rowSpan={2}
                              style={{
                                verticalAlign: "center",
                                paddingBottom: "7px",
                              }}
                            >
                              {idx !== data.length - 1 && " + "}
                            </td>
                          </tr>
                          <tr>
                            <td>
                              ( 1 + IRR) <sup>{idx + 1}</sup>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })}
                </td>
              </tr>
              <tr>
                <td>IRR</td>
                <td>≈</td>
                <td>{irr_value}</td>
              </tr>
            </tbody>
          </table>
          <section className="container_light">
            <p className="text_small">
              Catatan: Pada kasus ini nilai IRR adalah pendekatan dari solusi
              persamaan polinomial orde 6 (tergantung rentang jumlah tahun),
              angka IRR yang dihasilkan mungkin tidak menghasilkan NPV yang
              eksak sama dengan 0.
            </p>
          </section>
        </main>
      );
    }

    const chart_js_data = {
      labels,
      datasets: [
        {
          label: ina,
          labels,
          data,
          backgroundColor: color_default,
        },
      ],
    };

    item.chart_js_data = chart_js_data;
    item.npv_value = npv_value;
    item.npv_info = npv_info;
    item.irr_value = irr_value;
    item.irr_info = irr_info;

    return item;
  });

  const modal_npv_content = modal_npv && (
    <Modal modalSize="large" id="modal" isOpen={modal_npv} onClose={toggle_npv}>
      <div className="box-body">
        <main className="max_height">{npv_info}</main>
      </div>
    </Modal>
  );

  const modal_irr_content = modal_irr && (
    <Modal modalSize="large" id="modal" isOpen={modal_irr} onClose={toggle_irr}>
      <div className="box-body">
        <main className="max_height">{irr_info}</main>
      </div>
    </Modal>
  );

  const render_chart_item = (item, idx) => {
    const {
      is_npv_kai,
      npv_value,
      irr_value,
      chart_type,
      x_axis_field_key,
      chart_js_data,
    } = item;

    let chartData = { ...chart_js_data };
    const label_array = chartData.datasets[0]?.labels || chartData.labels || [];
    const value_array = chartData.datasets[0]?.data || [];
    const color_array = generate_array_color(label_array);

    if (chartData.datasets[0]) {
      chartData.datasets[0].backgroundColor = color_array;
      chartData.datasets[0].borderColor = color_array;
      chartData.datasets[0].borderWidth = 1;
    }

    const is_circle_chart = ["pie", "doughnut", "polarArea"].includes(
      chart_type,
    );
    const isLongChart = ["bar", "line", "area"].includes(chart_type);
    const chartHeight = isLongChart ? 150 : undefined;

    const numericData = value_array.map((d) =>
      typeof d === "object" && d !== null ? d.y : d,
    );
    const total = numericData.reduce((sum, val) => sum + val, 0);

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: !isLongChart,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
      },
      scales: isLongChart
        ? {
            x: {
              beginAtZero: true,
              ticks: { display: chart_type === "line" },
            },
            y: {
              beginAtZero: true,
            },
          }
        : {},
      onClick: (event, element) => {
        try {
          const index = element?.[0]?.index;
          const value = label_array?.[index];
          if (value !== undefined) {
            on_filter_isi_layer(value, x_axis_field_key);
          }
        } catch (e) {}
      },
    };

    const custom_legend = (
      <section
        className="container background_white"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "8px",
          maxHeight: "200px",
          overflowY: "auto",
          overflowX: "hidden",
          borderRadius: "5px",
        }}
      >
        <table>
          <tbody>
            {(label_array || []).map((label, index) => {
              const count = numericData[index];
              const percentage =
                total > 0 ? ((count / total) * 100).toFixed(1) : "0.0";
              return (
                <tr
                  key={index}
                  style={{ whiteSpace: "nowrap", cursor: "pointer" }}
                  onClick={() => on_filter_isi_layer(label, x_axis_field_key)}
                >
                  <td
                    style={{
                      verticalAlign: "top",
                      paddingRight: "8px",
                      textAlign: "center",
                      width: "30px",
                    }}
                  >
                    <div
                      style={{
                        width: "15px",
                        height: "15px",
                        backgroundColor: color_array[index] || "transparent",
                        margin: "0 auto",
                      }}
                    />
                  </td>
                  <td>
                    <div>
                      <p className="text_medium">{label} </p>
                      <span className="text_small">{count}</span>{" "}
                      <span className="text_small sub_color">
                        ({percentage} %)
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    );

    let final_content;

    if (is_circle_chart) {
      final_content = (
        <main
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <section style={{ width: "77px" }}>
            <Chart
              type={chart_type}
              data={chartData}
              options={chartOptions}
              style={{ height: chartHeight, maxHeight: chartHeight }}
            />
          </section>
          <section style={{ width: "100%" }}>{custom_legend}</section>
        </main>
      );
    } else {
      final_content = (
        <main style={{ width: "100%" }}>
          <div
            style={{
              overflowX: "auto",
              paddingBottom: "1rem",
            }}
          >
            <div
              style={{ minWidth: `${label_array.length * 30}px` }}
              className="container background_white"
            >
              <Chart
                type={chart_type}
                data={chartData}
                options={chartOptions}
                style={{ height: chartHeight, maxHeight: chartHeight }}
              />
            </div>
          </div>
          {custom_legend}
        </main>
      );
    }

    return (
      <section
        key={item._id || idx}
        className="container background_grey_light outline_grey margin_bottom"
      >
        <h2 className="badge background_white margin_bottom">{item.ina}</h2>
        {final_content}

        {is_npv_kai && (
          <>
            <section className="container_light margin_bottom">
              <section>
                <h2 className="text_medium inline margin_right">
                  PERHITUNGAN NPV
                </h2>
                <div
                  className="badge_pill_small background_black inline cursor_pointer"
                  onClick={toggle_npv}
                >
                  i
                </div>
              </section>

              <br />
              <p className="text_bold">
                {new Intl.NumberFormat("id-ID", {
                  style: "decimal",
                }).format(npv_value)}
              </p>
              {!capex_value && (
                <section className="container_light background_red">
                  <p className="text_small margin_bottom">
                    Nilai CAPEX belum dimuat, nilai NPV & IRR belum final, klik
                    tombol di bawah untuk memuat nilai CAPEX terlebih dahulu.
                  </p>
                  <GET_CAPEX />
                </section>
              )}
            </section>
            <section className="container_light margin_bottom">
              <section>
                <h2 className="text_medium inline margin_right">
                  PERHITUNGAN IRR
                </h2>
                <div
                  className="badge_pill_small background_black inline cursor_pointer"
                  onClick={toggle_irr}
                >
                  i
                </div>
              </section>

              <br />
              <h1 className="text_bold">{irr_value}</h1>
            </section>
          </>
        )}
      </section>
    );
  };

  const charts_without_folder = chart_list.filter(
    (c) =>
      !c.folder_key ||
      !folder_chart_list.some((folder) => folder.key === c.folder_key),
  );

  return (
    <main>
      {modal_npv_content}
      {modal_irr_content}

      {/* Expand/Collapse All Button */}
      {chart_list.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "16px",
          }}
        >
          <button
            onClick={toggle_all_folders}
            className="text_small sub_color cursor_pointer"
            style={{
              background: "none",
              border: "none",
              textDecoration: "underline",
            }}
          >
            Expand / Collapse All
          </button>
        </div>
      )}

      {/* Folder Charts */}
      {folder_chart_list.map((folder, f_idx) => {
        const charts_in_folder = chart_list.filter(
          (c) => c.folder_key === folder.key,
        );
        if (charts_in_folder.length === 0) return null;

        const is_collapsed = collapsed_folders[folder.key];

        return (
          <div key={folder._id || f_idx} style={{ marginBottom: "16px" }}>
            <button
              className="button background_grey_light margin_bottom"
              onClick={() => toggle_folder(folder.key)}
            >
              <span className="text_medium">{folder.title}</span>
              {is_collapsed ? (
                // Collapsed State: Chevron Down
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              ) : (
                // Expanded State: Chevron Up
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
              )}
            </button>

            {!is_collapsed && (
              <div>
                {charts_in_folder.map((item, idx) =>
                  render_chart_item(item, idx),
                )}
              </div>
            )}
            <hr />
          </div>
        );
      })}

      {/* Uncategorized Charts */}
      {charts_without_folder.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          {folder_chart_list.length > 0 && (
            <button
              className="button background_grey_light margin_bottom"
              onClick={() => toggle_folder("uncategorized")}
            >
              <span className="text_medium">Uncategorized</span>
              {collapsed_folders["uncategorized"] ? (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
              )}
            </button>
          )}

          {!collapsed_folders["uncategorized"] && (
            <div>
              {charts_without_folder.map((item, idx) =>
                render_chart_item(item, idx),
              )}
            </div>
          )}
        </div>
      )}
    </main>
  );
};

const mapStateToProps = (state) => ({
  map: state.map,
  layer: state.layer,
  properties: state.properties,
  auth: state.auth,
});

export default connect(mapStateToProps, { replace_features, filter_isi_layer })(
  CUSTOM_CHART,
);
